import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, computed, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { remapEchartsColors, watchBodyDarkMode$ } from '@examdojo/core/widget';
import { EChartsOption } from 'echarts';
import { BarChart, ScatterChart } from 'echarts/charts';
import {
  DataZoomInsideComponent,
  DataZoomSliderComponent,
  GridComponent,
  MarkAreaComponent,
  MarkLineComponent,
  TooltipComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { SVGRenderer } from 'echarts/renderers';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';

import { HOVER_VALUE_CHART_COLOR, type BinSelection, type HistogramOverlay } from '../measures/measures-highlight.util';
import {
  DEFAULT_HISTOGRAM_DISPLAY,
  Y_AXIS_LABELS,
  type HistogramDisplayConfig,
  type HistogramParams,
} from './histogram-chart.model';
import { computeHistogram } from './histogram-chart.util';

echarts.use([
  BarChart,
  ScatterChart,
  SVGRenderer,
  GridComponent,
  TooltipComponent,
  MarkLineComponent,
  MarkAreaComponent,
  DataZoomInsideComponent,
  DataZoomSliderComponent,
]);

@Component({
  selector: 'dojo-histogram-chart',
  standalone: true,
  imports: [NgxEchartsDirective],
  providers: [provideEchartsCore({ echarts })],
  template: `
    <div
      echarts
      [options]="adaptedOptions()"
      [initOpts]="initOpts"
      (chartClick)="onChartClick($event)"
      class="histogram-chart"
    ></div>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }

    .histogram-chart {
      width: 100%;
      height: 100%;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HistogramChartComponent {
  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    this.isDark = toSignal(watchBodyDarkMode$(this.document), { initialValue: false });
  }

  private readonly isDark;

  readonly params = input.required<HistogramParams>();
  readonly display = input<HistogramDisplayConfig>({});
  readonly overlay = input<HistogramOverlay>({ markLines: [], markAreas: [] });
  readonly selectedBin = input<BinSelection | null>(null);
  readonly hoveredValue = input<number | null>(null);

  readonly binSelect = output<BinSelection>();
  readonly initOpts = { renderer: 'svg' } as const;

  private readonly computedHistogram = computed(() => computeHistogram(this.params().data, this.params().config));

  readonly chartOptions = computed<EChartsOption>(() => {
    const display = { ...DEFAULT_HISTOGRAM_DISPLAY, ...this.display() };
    const yAxisType = this.params().config.yAxisType;
    const yAxisLabel = display.yAxisLabel ?? Y_AXIS_LABELS[yAxisType];
    const isPercent = yAxisType === 'relative-frequency' || yAxisType === 'frequency-density';

    const { labels, values, binEdges } = this.computedHistogram();

    const overlay = this.overlay();
    const selectedBin = this.selectedBin();
    const hoveredValue = this.hoveredValue();

    return {
      axisPointer: { type: 'shadow', link: [{ xAxisIndex: 'all' }] },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(0, 0, 0, 0.04)' } },
        backgroundColor: '#fff',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        extraCssText: 'box-shadow: 0 2px 8px rgba(0,0,0,0.08);',
        padding: [10, 14],
        textStyle: { fontSize: 12, color: '#374151' },
        formatter: (tooltipParams) => {
          const param = (tooltipParams as Array<{ value: number; dataIndex: number; name: string }>)[0];
          if (!param) {
            return '';
          }
          const total = values.reduce((a, b) => a + b, 0);
          const pct = total > 0 ? ((param.value / total) * 100).toFixed(1) : '0';
          const bold = (v: string | number) => `<span style="font-weight:600;color:#111827">${v}</span>`;
          return [
            `<div style="margin-bottom:4px;font-weight:600;color:#6366f1">${param.name}</div>`,
            `<div style="display:flex;justify-content:space-between;gap:16px"><span style="color:#6b7280">${yAxisLabel}</span>${bold(
              param.value,
            )}</div>`,
            `<div style="display:flex;justify-content:space-between;gap:16px"><span style="color:#6b7280">Percentage</span>${bold(
              `${pct}%`,
            )}</div>`,
          ].join('');
        },
      },
      grid: {
        top: 40,
        bottom: 25,
        left: 40,
        right: 30,
        containLabel: true,
      },
      xAxis: [
        {
          type: 'category',
          data: labels,
          name: display.xAxisLabel,
          nameLocation: 'middle',
          nameGap: labels.length > 15 ? 55 : 30,
          nameTextStyle: {
            fontSize: 12,
            color: '#6b7280',
          },
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#d1d5db' } },
          axisLabel: {
            fontSize: 11,
            color: '#6b7280',
            rotate: labels.length > 15 ? 45 : 0,
            hideOverlap: true,
          },
        },
        // Hidden value axis aligned with the category axis — used for overlay marks.
        // Always present so the chart structure stays stable when overlays appear/disappear.
        ...(binEdges.length >= 2
          ? [
              {
                type: 'value' as const,
                min: binEdges[0],
                max: binEdges[binEdges.length - 1],
                show: false,
                axisPointer: { show: false },
              },
            ]
          : []),
      ],
      yAxis: {
        type: 'value',
        name: yAxisLabel,
        nameLocation: 'middle',
        nameGap: 35,
        nameTextStyle: {
          fontSize: 12,
          color: '#6b7280',
        },
        axisTick: { show: false },
        axisLine: { show: false },
        axisPointer: { show: false },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
        axisLabel: {
          fontSize: 12,
          color: '#6b7280',
          formatter: isPercent ? (v: number) => `${v}%` : undefined,
        },
      },
      ...(labels.length >= 50
        ? {
            dataZoom: [
              { type: 'inside', start: 0, end: 10 },
              { type: 'slider', start: 0, end: 10, height: 20, bottom: 30 },
            ],
          }
        : {}),
      series: [
        {
          type: 'bar',
          xAxisIndex: 0,
          data: values.map((v, i) => {
            const isSelected = selectedBin?.dataIndex === i;
            const isHovered =
              hoveredValue !== null &&
              i < binEdges.length - 1 &&
              hoveredValue >= binEdges[i] &&
              hoveredValue < binEdges[i + 1];
            return {
              value: v,
              itemStyle: isSelected ? { color: '#fbbf24' } : isHovered ? { color: HOVER_VALUE_CHART_COLOR } : undefined,
            };
          }),
          barWidth: '60%',
          itemStyle: {
            color: display.barColor ?? '#6366f1',
            borderRadius: [4, 4, 0, 0],
          },
          label: display.showFrequencyLabels
            ? {
                show: true,
                position: 'top',
                fontSize: 11,
                color: '#6b7280',
                formatter: isPercent ? (p: { value: number }) => `${p.value}%` : undefined,
              }
            : { show: false },
        },
        buildOverlaySeries(overlay, binEdges.length >= 2),
      ],
    };
  });

  readonly adaptedOptions = computed(() => remapEchartsColors(this.chartOptions(), this.document, this.isDark()));

  onChartClick(event: { seriesType?: string; dataIndex: number }): void {
    if (event.seriesType !== 'bar') {
      return;
    }
    const { binEdges } = this.computedHistogram();
    const i = event.dataIndex;
    if (i < 0 || i >= binEdges.length - 1) {
      return;
    }
    this.binSelect.emit({ dataIndex: i, min: binEdges[i], max: binEdges[i + 1] });
  }
}

// ---------------------------------------------------------------------------
// Overlay helpers
// ---------------------------------------------------------------------------

/**
 * Build an invisible scatter series on the secondary value xAxis (index 1).
 * markLine and markArea on this series use raw numeric `xAxis` values,
 * which align precisely with the category bars because both axes span
 * the same grid and the same data range.
 *
 * Always returns a series so the chart structure stays stable when overlays
 * appear/disappear on hover, preventing y-axis layout shifts.
 */
function buildOverlaySeries(overlay: HistogramOverlay, hasSecondXAxis: boolean): Record<string, unknown> {
  return {
    type: 'scatter',
    xAxisIndex: hasSecondXAxis ? 1 : 0,
    z: 0,
    data: [],
    silent: true,
    symbolSize: 0,
    tooltip: { show: false },
    markLine: {
      symbol: 'none',
      animation: false,
      data: overlay.markLines.map((l) => ({
        xAxis: l.value,
        label: {
          formatter: l.label,
          position: 'end',
          offset: [0, -10],
          fontSize: 10,
          color: l.color,
        },
        lineStyle: {
          color: l.color,
          type: l.lineStyle,
          width: 1.5,
        },
      })),
    },
    markArea: {
      animation: false,
      data: overlay.markAreas.map((a) => [
        {
          xAxis: a.from,
          itemStyle: { color: a.color },
          label: {
            show: !!a.label,
            position: 'top',
            formatter: a.label,
            color: '#6b7280',
            fontSize: 10,
          },
        },
        {
          xAxis: a.to,
        },
      ]),
    },
  };
}
